import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spinner, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { recordingApi } from 'api';

const VideoPlaybackModal = ({ show, onHide, shipmentId }) => {
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const videoRef = useRef(null);
  const onHideRef = useRef(onHide);

  useEffect(() => {
    onHideRef.current = onHide;
  }, [onHide]);

  useEffect(() => {
    if (!show || !shipmentId) {
      setVideos([]);
      setSelectedVideo(null);
      return;
    }

    let cancelled = false;
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const data = await recordingApi.getPlaybackList(shipmentId);
        if (!cancelled) {
          const list = Array.isArray(data) ? data : [];
          setVideos(list);
          setSelectedVideo(list.length === 1 ? list[0] : null);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error('Failed to load videos');
          onHideRef.current?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchVideos();
    return () => {
      cancelled = true;
    };
  }, [show, shipmentId]);

  const formatDuration = ms => {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  const formatSize = bytes => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFilename = url => {
    try {
      const path = new URL(url).pathname;
      return decodeURIComponent(path.split('/').pop());
    } catch {
      return 'video.webm';
    }
  };

  const handleBack = () => {
    setSelectedVideo(null);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-0">
          {selectedVideo && videos.length > 1 ? (
            <>
              <FontAwesomeIcon
                icon="arrow-left"
                className="cursor-pointer me-2 fs--1"
                onClick={handleBack}
              />
              Picking Video
            </>
          ) : (
            'Picking Videos'
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : selectedVideo ? (
          <>
            <video
              ref={videoRef}
              src={selectedVideo.url}
              controls
              autoPlay
              style={{
                width: '100%',
                display: 'block',
                backgroundColor: '#000',
              }}
            />
            <div className="d-flex justify-content-between px-3 py-2 text-muted fs--1">
              <span>
                {getFilename(selectedVideo.url)} &middot;{' '}
                {formatDuration(selectedVideo.duration_ms)} &middot;{' '}
                {formatSize(selectedVideo.file_size)}
              </span>
              <span>{new Date(selectedVideo.created).toLocaleString()}</span>
            </div>
          </>
        ) : videos.length > 0 ? (
          <ListGroup variant="flush">
            {videos.map((video, index) => (
              <ListGroup.Item
                key={index}
                action
                onClick={() => setSelectedVideo(video)}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <FontAwesomeIcon
                    icon="play-circle"
                    className="text-info me-2"
                  />
                  <span>
                    Recording {index + 1} &middot;{' '}
                    {formatDuration(video.duration_ms)} &middot;{' '}
                    {formatSize(video.file_size)}
                  </span>
                </div>
                <small className="text-muted">
                  {new Date(video.created).toLocaleString()}
                </small>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <div className="text-center text-muted py-4">No videos found</div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default VideoPlaybackModal;
